import { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Loader2, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";
let rzpLoader = null;
function loadRazorpay() {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.Razorpay) return Promise.resolve();
  if (rzpLoader) return rzpLoader;
  rzpLoader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      rzpLoader = null;
      reject(new Error("Razorpay script failed to load"));
    };
    document.body.appendChild(s);
  });
  return rzpLoader;
}

export default function MatchaPassPurchaseModal({ open, onOpenChange, tier }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [step, setStep] = useState("form"); // form | done
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep("form");
        setForm({ name: "", email: "", phone: "" });
      }, 250);
      return () => clearTimeout(t);
    }
    // Preload Razorpay script on open
    loadRazorpay().catch(() => {});
  }, [open]);

  useEffect(() => {
    api.get("/pass-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const tierData = useMemo(() => {
    if (!config || !tier) return null;
    return config.tiers.find((t) => t.id === tier);
  }, [config, tier]);

  const handlePay = useCallback(async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email and phone are needed.");
      return;
    }
    setSubmitting(true);
    try {
      await loadRazorpay();
      const { data: order } = await api.post("/razorpay/order", {
        tier,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "Hontō Matcha",
        description: order.tier_label,
        prefill: {
          name: form.name.trim(),
          email: form.email.trim(),
          contact: form.phone.trim(),
        },
        notes: { tier },
        theme: { color: "#2C4C3B" },
        method: { upi: true, card: true, netbanking: true, wallet: false },
        handler: async (response) => {
          try {
            await api.post("/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setStep("done");
            toast.success("Payment confirmed.");
            trackEvent("razorpay_payment_success", {
              tier,
              value: tierData?.amount_inr,
              currency: "INR",
              transaction_id: response.razorpay_payment_id,
            });
          } catch (err) {
            toast.error("Couldn't verify payment. We'll reconcile it manually.");
          }
        },
        modal: {
          ondismiss: () => {
            toast("Payment cancelled.");
            setSubmitting(false);
          },
        },
      };
      // Open Razorpay overlay
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed.");
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start payment.";
      toast.error(typeof msg === "string" ? msg : "Payment couldn't start.");
    } finally {
      // Leave submitting=true while Razorpay modal is open; ondismiss handles reset.
    }
  }, [form, tier, tierData]);

  if (!tierData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md bg-[#F7F5F0] border border-border rounded-3xl">
          <div className="py-8 text-center" data-testid="pass-modal-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#2C4C3B]" />
            <p className="mt-3 text-sm text-[#5C7365]">Preparing pass details…</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#F7F5F0] border border-border rounded-3xl p-0 overflow-hidden gap-0"
        data-testid="pass-modal"
      >
        <DialogTitle className="sr-only">Matcha Pass · {tierData.label}</DialogTitle>
        <DialogDescription className="sr-only">
          Secure checkout for the {tierData.label}. Pay via UPI, card, or netbanking through Razorpay.
        </DialogDescription>
        <div className="px-8 pt-8">
          <div className="flex items-center justify-between">
            <p className="overline text-[#C47253]">
              {step === "done" ? "Pass active" : `Matcha Pass · ${tierData.label}`}
            </p>
            <span className="font-serif italic text-[#2C4C3B] text-sm">本当</span>
          </div>
        </div>

        {step === "form" ? (
          <form onSubmit={handlePay} className="px-8 pb-8 pt-5" data-testid="pass-modal-form">
            <h2 className="font-serif text-3xl text-[#1A2B22] leading-tight">
              Pay ₹{tierData.amount_inr.toLocaleString("en-IN")}
              <span className="block text-sm font-sans text-[#5C7365] mt-1 italic">
                {tierData.discount} off · {tierData.label}
              </span>
            </h2>
            <p className="text-sm text-[#5C7365] mt-4">
              Secure checkout via Razorpay. UPI, cards, netbanking — pay however you like.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#5C7365]">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="pass-input-name"
                  className="mt-1.5 h-11 rounded-xl bg-white"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#5C7365]">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="pass-input-email"
                  className="mt-1.5 h-11 rounded-xl bg-white"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#5C7365]">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  placeholder="+91"
                  data-testid="pass-input-phone"
                  className="mt-1.5 h-11 rounded-xl bg-white"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="pass-modal-submit"
              className="mt-6 w-full rounded-full bg-primary hover:bg-[#1F382B] text-primary-foreground h-12 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout</>
              ) : (
                <>Pay ₹{tierData.amount_inr.toLocaleString("en-IN")} <ArrowRight className="ml-2 w-4 h-4" /></>
              )}
            </Button>
            <p className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#5C7365]">
              <Shield className="w-3 h-3" /> Razorpay encrypted · INR
            </p>
          </form>
        ) : (
          <div className="px-8 py-14 text-center" data-testid="pass-modal-success">
            <div className="w-14 h-14 rounded-full bg-[#2C4C3B] flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-[#F7F5F0]" strokeWidth={2.5} />
            </div>
            <h2 className="mt-6 font-serif text-3xl text-[#1A2B22]">
              Your pass is live.
            </h2>
            <p className="mt-4 text-[#5C7365] max-w-md mx-auto text-sm">
              Payment confirmed. We&apos;ll email your digital pass shortly. Bring it on your first visit.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              data-testid="pass-modal-close"
              className="mt-8 rounded-full bg-primary hover:bg-[#1F382B] text-primary-foreground h-12 px-8 text-xs tracking-[0.2em] uppercase"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
